/**
 * @file Parses what the `queryAnalytics` callable answered into a `NamedQueryResult`, or
 * `undefined` when any field falls outside the shared contract. Enum fields are matched by `===`
 * against `NAMED_QUERY_IDS`, `ANALYTICS_SCOPE_LEVELS`, `NAMED_QUERY_PARAMETER_ENUMS`,
 * `NAMED_QUERY_SERIES_PARAMETER_ENUMS`, `ANALYTICS_PERIODS` and `FACT_TABLE_IDS`, and the result is a
 * new object built only from the contract's fields, so an extra key such as the loop's
 * `userNavigatedAway` annotation is dropped. A series answer's `grain` and `group` are optional and
 * kept when present; one present outside its enum makes the whole result `undefined`.
 * `refusedColumns` is required: each item names its column and binding `key` as strings with
 * `refusal === 'notConfigured'`, and one item outside that shape makes the whole result `undefined`.
 * Consumed by `runNamedAnalyticsQuery`, which refuses an unparseable answer, and by
 * `analyticsQueryProvenanceOf`, which drops provenance for one. It lives in `types/src` so React
 * reads the same parser.
 */
import { FACT_TABLE_IDS } from './Analytics.Catalog.types';
import { ANALYTICS_PERIODS, ANALYTICS_SCOPE_LEVELS, NAMED_QUERY_IDS, NAMED_QUERY_PARAMETER_ENUMS, NAMED_QUERY_SERIES_PARAMETER_ENUMS } from './Analytics.NamedQuery.types';
import type { FactBuildProvenance, NamedQueryCell, NamedQueryParameters, NamedQueryRefusedColumn, NamedQueryResult, ResolvedAnalyticsPeriod } from './Analytics.NamedQuery.types';

const fieldsOf = (value: unknown): Record<string, unknown> | undefined =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? Object.fromEntries(Object.entries(value)) : undefined;

const isNamedQueryCell = (value: unknown): value is NamedQueryCell =>
    value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const parametersOf = (value: unknown): NamedQueryParameters | undefined => {
    const fields = fieldsOf(value);
    const period = NAMED_QUERY_PARAMETER_ENUMS.period.find((member) => member === fields?.period);
    const grain = NAMED_QUERY_SERIES_PARAMETER_ENUMS.grain.find((member) => member === fields?.grain);
    const group = NAMED_QUERY_SERIES_PARAMETER_ENUMS.group.find((member) => member === fields?.group);
    if (!fields || period === undefined) return undefined;
    if ((fields.grain !== undefined && grain === undefined) || (fields.group !== undefined && group === undefined)) return undefined;
    return { period, ...(grain === undefined ? {} : { grain }), ...(group === undefined ? {} : { group }) };
};

const periodOf = (value: unknown): ResolvedAnalyticsPeriod | undefined => {
    const fields = fieldsOf(value);
    const period = ANALYTICS_PERIODS.find((member) => member === fields?.period);
    if (!fields || period === undefined) return undefined;
    const { from, to, label } = fields;
    return typeof from === 'string' && typeof to === 'string' && typeof label === 'string' ? { period, from, to, label } : undefined;
};

const buildOf = (value: unknown): FactBuildProvenance | undefined => {
    const fields = fieldsOf(value);
    const factTable = FACT_TABLE_IDS.find((member) => member === fields?.factTable);
    if (!fields || factTable === undefined) return undefined;
    const { buildId, generation, cutTime, builtAt } = fields;
    return typeof buildId === 'string' && typeof generation === 'number' && typeof cutTime === 'string' && typeof builtAt === 'string'
        ? { factTable, buildId, generation, cutTime, builtAt }
        : undefined;
};

const rowOf = (value: unknown): Record<string, NamedQueryCell> | undefined => {
    const fields = fieldsOf(value);
    if (!fields) return undefined;
    const entries = Object.entries(fields);
    const cells = entries.flatMap(([key, cell]) => (isNamedQueryCell(cell) ? [[key, cell] as const] : []));
    return cells.length === entries.length ? Object.fromEntries(cells) : undefined;
};

const refusedColumnOf = (value: unknown): NamedQueryRefusedColumn | undefined => {
    const fields = fieldsOf(value);
    if (!fields) return undefined;
    const { column, refusal, key } = fields;
    return typeof column === 'string' && refusal === 'notConfigured' && typeof key === 'string' ? { column, refusal, key } : undefined;
};

const itemsOf = <T>(value: unknown, parse: (item: unknown) => T | undefined): T[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    const items = value.flatMap((item) => {
        const parsed = parse(item);
        return parsed === undefined ? [] : [parsed];
    });
    return items.length === value.length ? items : undefined;
};

export function parseNamedQueryResult(value: unknown): NamedQueryResult | undefined {
    const fields = fieldsOf(value);
    if (!fields) return undefined;
    const query = NAMED_QUERY_IDS.find((id) => id === fields.query);
    const level = ANALYTICS_SCOPE_LEVELS.find((member) => member === fields.level);
    const parameters = parametersOf(fields.parameters);
    const period = periodOf(fields.period);
    const { snapshotId } = fields;
    const builds = itemsOf(fields.builds, buildOf);
    const rows = itemsOf(fields.rows, rowOf);
    const refusedColumns = itemsOf(fields.refusedColumns, refusedColumnOf);
    if (query === undefined || level === undefined || !parameters || !period) return undefined;
    if (typeof snapshotId !== 'number' || !Number.isFinite(snapshotId) || !builds || !rows || !refusedColumns) return undefined;
    return { query, parameters, level, period, snapshotId, builds, rows, refusedColumns };
}
